import * as React from 'react';

import { IconButton } from '../IconButton/IconButton';
import CrossIcon from '../../../../../res/provider/ui/image/shapes/cross.svg';
import { ClassNameBuilder } from '../../utils/ClassNameBuilder';
import { mergeRefs } from '../../utils/mergeRefs';
import { Icon } from '../Icon/Icon';

import Styles from './TextInput.module.scss';

export type TextInputOnChange = (event: { name: string; value: string }) => void;

interface Props
  extends Omit<
    React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'onChange'
  > {
  clearControl?: boolean;
  error?: boolean;
  onChange?: TextInputOnChange;
  className?: string;
  focusOnMount?: boolean;
  capitalize?: boolean;
  icon?: string;
}

// This is used in the control center
export const TextInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    clearControl,
    className,
    capitalize = false,
    focusOnMount,
    icon,
    onChange,
    error,
    ...rest
  } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [inputRef.current]);

  const containerClass = new ClassNameBuilder(Styles, 'inputContainer')
    .add([Styles.error, !!error])
    .add([className, !!className])
    .toString();

  const inputClass = new ClassNameBuilder(Styles, 'inputText')
    .add([Styles.clearControlPadding, clearControl])
    .add([Styles.iconPadding, icon !== undefined])
    .add([Styles.capitalize, capitalize])
    .toString();

  const clearButtonHandler = () => {
    if (inputRef.current) {
      onChange?.({ name: rest.name || '', value: '' });
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.({ name: event.target.name, value: event.target.value });
  };

  return (
    <div className={containerClass}>
      {icon && (
        <div className={Styles.inputIcon}>
          <Icon src={icon} />
        </div>
      )}
      <input
        type="text"
        className={inputClass}
        onChange={onChangeHandler}
        ref={mergeRefs([inputRef, ref])}
        {...rest}
      />
      {clearControl && (
        <IconButton image={CrossIcon} className={Styles.inputButton} onClick={clearButtonHandler} />
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';
